import {
  type ReactNode,
  useEffect,
  useState,
} from 'react'
import {
  applyTheme,
  getInitialResolvedTheme,
  getInitialThemeMode,
  getThemeStorageKey,
  getSystemTheme,
  isThemeMode,
  type ResolvedTheme,
  type ThemeMode,
} from '@/lib/theme'
import { ThemeContext } from '@/theme/theme-context'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeMode>(getInitialThemeMode)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(getInitialResolvedTheme)

  useEffect(() => {
    setResolvedTheme(applyTheme(theme))
  }, [theme])

  useEffect(() => {
    if (theme !== 'system') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const syncSystemTheme = () => {
      setResolvedTheme(getSystemTheme())
      applyTheme('system')
    }

    mediaQuery.addEventListener('change', syncSystemTheme)

    return () => {
      mediaQuery.removeEventListener('change', syncSystemTheme)
    }
  }, [theme])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== getThemeStorageKey()) {
        return
      }

      const nextTheme = isThemeMode(event.newValue) ? event.newValue : 'system'
      setTheme(nextTheme)
      setResolvedTheme(applyTheme(nextTheme))
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  return (
    <ThemeContext.Provider value={{ resolvedTheme, setTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  )
}
